// Extend flight (campaign lifecycle 2b, 2026-10-21): add one flight to a live or paused
// campaign, keeping its Meta or The Trade Desk objects and its history.
//
// Two faces. With no extension open it is a small form: start, end and the new flight's
// gross budget, the provider's note, and whatever the route refuses in the route's own
// words (the public Meta floor, TTD's per-audience split, the 180-day horizon). With one
// open or failed it says where that one is, offers Retry when the server says a retry can
// help and Cancel while the extension is not yet in the plan, and never offers a way to
// release a create SAM cannot confirm (Gio, 2026-10-21: duplicate the campaign instead).

import { useId, useRef, useState } from 'react';
import { ApiError } from '@/api/errors';
import type { FailureView } from '@/api/types.gen';
import type { CampaignFlightExtension, CampaignPlan } from '@/api/types';
import {
  useCancelCampaignFlightExtension,
  useExtendCampaignFlight,
  useRetryCampaignFlightExtension,
} from '@/api/manage-hooks';
import { FailureNotice } from '@/components/failure-notice';
import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { fmtDay } from '@/lib/datetime';
import {
  EXTENSION_UNCONFIRMED_SENTENCE,
  addCalendarDays,
  earliestExtensionStart,
  extensionCreateUnconfirmed,
  extensionInputIssue,
  flightExtensionSignal,
} from '@/lib/flight-extension';
import { reportActionError } from '@/lib/report-action-error';

export type ExtendTarget = 'meta' | 'ttd';

export interface ExtendFlightDialogProps {
  campaignId: string;
  campaignName: string;
  /** The plan revision the request extends; the route answers 409 on a mismatch. */
  revision: number;
  target: ExtendTarget;
  /** The campaign's requested state is paused: on Meta it stays paused until Resume. */
  paused: boolean;
  /** The last flight's end, `YYYY-MM-DD`; the new flight starts after it. */
  lastFlightEnd: string | null;
  /** The last flight's gross budget in dollars, offered as the new flight's. */
  suggestedBudget: number | null;
  /** New York's today, `YYYY-MM-DD`. */
  todayNy: string;
  /** The furthest end the server schedules (New York today + 180 days). */
  latestEnd: string;
  /** The newest extension while it is open or failed: shown instead of the form. */
  current: CampaignFlightExtension | null;
  onClose: () => void;
  /** After an accepted request. `plan` is the committed plan (Meta), null when queued (TTD). */
  onExtended: (result: { extension: CampaignFlightExtension; plan: CampaignPlan | null }) => void;
}

const PROVIDER: Record<ExtendTarget, string> = { meta: 'Meta', ttd: 'The Trade Desk' };

/** The route's refusal in its own words: `failure` when it sent one, else its message. */
function refusalOf(error: unknown): { failure: FailureView | null; message: string } {
  const message = reportActionError(error, { operation: 'campaign.extend_flight' });
  if (error instanceof ApiError && error.details && typeof error.details === 'object') {
    const failure = (error.details as { failure?: unknown }).failure;
    if (failure && typeof failure === 'object' && 'headline' in failure && 'code' in failure) {
      return { failure: failure as FailureView, message };
    }
  }
  return { failure: null, message };
}

function dollars(n: number): string {
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
}

export function ExtendFlightDialog(props: ExtendFlightDialogProps) {
  const { current, onClose } = props;
  return (
    <ResponsiveOverlay label="Extend flight" kind="form" className="modal-host" onClose={onClose}>
      <div className="modal-card extend-flight-dialog" style={{ maxWidth: 520 }}>
        {current ? <ExtensionStatus {...props} row={current} /> : <ExtendForm {...props} />}
      </div>
    </ResponsiveOverlay>
  );
}

function ProviderNote({ target, paused }: { target: ExtendTarget; paused: boolean }) {
  return (
    <div className="extend-flight-note" role="note">
      {target === 'meta' ? (
        <>
          <p>
            Meta’s lifetime budget is cumulative, so budget left unspent from earlier flights
            carries over into the new one. Meta picks the new flight up on its first day.
          </p>
          {paused && <p>This campaign is paused, and it stays paused until you resume it.</p>}
        </>
      ) : (
        <>
          <p>The new flight starts tomorrow at the earliest.</p>
          <p>
            The Trade Desk ad groups pause for the few minutes the extension takes, then follow the
            campaign’s schedule again.
          </p>
        </>
      )}
    </div>
  );
}

function ExtendForm({
  campaignId,
  campaignName,
  revision,
  target,
  paused,
  lastFlightEnd,
  suggestedBudget,
  todayNy,
  latestEnd,
  onClose,
  onExtended,
}: ExtendFlightDialogProps) {
  const ids = useId();
  const earliestStart = earliestExtensionStart(todayNy, lastFlightEnd);
  const [start, setStart] = useState(earliestStart);
  const [end, setEnd] = useState(() => {
    const twoWeeks = addCalendarDays(earliestStart, 13);
    return twoWeeks > latestEnd ? latestEnd : twoWeeks;
  });
  const [budget, setBudget] = useState<number>(
    suggestedBudget && suggestedBudget > 0 ? Math.round(suggestedBudget) : 0,
  );
  const [issue, setIssue] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<{ failure: FailureView | null; message: string } | null>(
    null,
  );
  // One key for this dialog's request, reused by every attempt: a POST whose answer was
  // lost replays the row it made instead of making a second extension.
  const idempotencyKey = useRef(`flight_extension_${crypto.randomUUID()}`);
  const extend = useExtendCampaignFlight();

  const submit = async () => {
    const budgetCents = Math.round(budget * 100);
    const local = extensionInputIssue({ start, end, budgetCents, earliestStart, latestEnd });
    setIssue(local);
    if (local) return;
    setRefusal(null);
    try {
      const response = await extend.mutateAsync({
        id: campaignId,
        body: {
          idempotency_key: idempotencyKey.current,
          expected_revision: revision,
          flight_name: null,
          start_date: start,
          end_date: end,
          budget_cents: budgetCents,
        },
      });
      onExtended({ extension: response.extension, plan: response.plan ?? null });
    } catch (error) {
      setRefusal(refusalOf(error));
    }
  };

  const provider = PROVIDER[target];
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      style={{ display: 'contents' }}
    >
      <div className="modal-hd">
        <div>
          <h2 className="modal-title">Extend flight</h2>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            Adds one flight to {campaignName || 'this campaign'} on {provider}, after its last
            flight
            {lastFlightEnd ? ` (${fmtDay(lastFlightEnd)})` : ''}. The campaign keeps its {provider}{' '}
            objects and its history.
          </div>
        </div>
        <button type="button" className="modal-x" aria-label="Close dialog" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="modal-body">
        <div
          className="stack-phone"
          style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}
        >
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="field-lbl" htmlFor={`${ids}-start`}>
              Start date
            </label>
            <input
              id={`${ids}-start`}
              type="date"
              className="input mono"
              value={start}
              min={earliestStart}
              max={latestEnd}
              required
              aria-describedby={`${ids}-start-hint`}
              onChange={(event) => {
                const next = event.target.value;
                setStart(next);
                if (next && end && end < next) setEnd(next);
                setIssue(null);
              }}
            />
            <div className="field-hint" id={`${ids}-start-hint`}>
              {fmtDay(earliestStart)} at the earliest
            </div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="field-lbl" htmlFor={`${ids}-end`}>
              End date
            </label>
            <input
              id={`${ids}-end`}
              type="date"
              className="input mono"
              value={end}
              min={start || earliestStart}
              max={latestEnd}
              required
              onChange={(event) => {
                setEnd(event.target.value);
                setIssue(null);
              }}
            />
          </div>
        </div>
        <div className="field" style={{ marginBottom: 12 }}>
          <label className="field-lbl" htmlFor={`${ids}-budget`}>
            New flight budget (gross)
          </label>
          <input
            id={`${ids}-budget`}
            className="input mono"
            inputMode="numeric"
            autoComplete="off"
            value={`$${budget.toLocaleString('en-US')}`}
            aria-describedby={`${ids}-budget-hint`}
            onChange={(event) => {
              const n = parseInt(event.target.value.replace(/[^\d]/g, ''), 10);
              setBudget(Number.isNaN(n) ? 0 : n);
              setIssue(null);
            }}
          />
          <div className="field-hint" id={`${ids}-budget-hint`}>
            Added to the campaign’s budget, which becomes the total of every flight.
          </div>
        </div>
        <ProviderNote target={target} paused={paused} />
        {issue && (
          <div role="alert" className="extend-flight-issue">
            {issue}
          </div>
        )}
        {refusal && (
          <div style={{ marginTop: 12 }}>
            <FailureNotice failure={refusal.failure} fallback={refusal.message} />
          </div>
        )}
      </div>
      <div className="modal-foot">
        <button type="button" className="btn" disabled={extend.isPending} onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={extend.isPending}>
          {extend.isPending ? 'Extending…' : 'Extend flight'}
        </button>
      </div>
    </form>
  );
}

/** What the open or failed extension is doing, in words. */
function statusSentence(row: CampaignFlightExtension, target: ExtendTarget): string {
  const provider = PROVIDER[target];
  if (extensionCreateUnconfirmed(row)) {
    return `SAM keeps looking for the flight at ${provider}. The campaign’s ad groups stay off until it is found and either recorded or removed.`;
  }
  switch (row.status) {
    case 'queued':
      return target === 'ttd'
        ? 'Queued. SAM adds the flight at The Trade Desk within a few minutes.'
        : 'Queued.';
    case 'running':
      return target === 'ttd'
        ? 'SAM is adding the flight at The Trade Desk. Its ad groups are paused until the flight is in the plan.'
        : `SAM is adding the flight at ${provider}.`;
    case 'committed':
      return `The flight is in the plan. SAM is bringing ${provider} in line with it.`;
    case 'failed':
      return row.activation_hold
        ? `The extension stopped, and the campaign’s ${provider} ad groups stay off until it is retried or cancelled.`
        : 'The extension stopped before anything changed.';
    default:
      return '';
  }
}

function ExtensionStatus({
  campaignId,
  target,
  onClose,
  row,
}: ExtendFlightDialogProps & { row: CampaignFlightExtension }) {
  const retry = useRetryCampaignFlightExtension();
  const cancel = useCancelCampaignFlightExtension();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [error, setError] = useState<{ failure: FailureView | null; message: string } | null>(null);
  const signal = flightExtensionSignal([row], Date.now());
  const unconfirmed = signal?.kind === 'unconfirmed';
  const headline = unconfirmed
    ? EXTENSION_UNCONFIRMED_SENTENCE
    : signal?.kind === 'attention'
      ? 'Extension needs attention'
      : 'Extending…';
  const cancelRequested = !!row.cancel_requested_at;
  const canCancel =
    !cancelRequested &&
    (row.status === 'queued' || row.status === 'running' || row.status === 'failed');
  // Once the server records the cancel (a refreshed row, e.g. after a lost answer), the
  // confirmation closes: a second POST would only erase the cancel's backoff.
  const confirming = confirmCancel && canCancel;
  const busy = retry.isPending || cancel.isPending;
  const run = async (action: 'retry' | 'cancel') => {
    setError(null);
    try {
      if (action === 'retry') await retry.mutateAsync({ id: campaignId, extensionId: row.id });
      else {
        await cancel.mutateAsync({ id: campaignId, extensionId: row.id });
        setConfirmCancel(false);
      }
    } catch (err) {
      setError(refusalOf(err));
    }
  };
  return (
    <>
      <div className="modal-hd">
        <div>
          <h2 className="modal-title">Extend flight</h2>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
            {row.flight_name} · {fmtDay(row.start_date)} → {fmtDay(row.end_date)} ·{' '}
            {dollars(row.budget_cents / 100)} gross
          </div>
        </div>
        <button type="button" className="modal-x" aria-label="Close dialog" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="modal-body" role="status" aria-live="polite">
        <div className="extend-flight-status" data-kind={signal?.kind ?? 'extending'}>
          {headline}
        </div>
        <p style={{ fontSize: 12.5, lineHeight: 1.45, margin: '6px 0 0' }}>
          {statusSentence(row, target)}
        </p>
        {cancelRequested && (
          <p style={{ fontSize: 12.5, lineHeight: 1.45, margin: '6px 0 0' }}>
            Cancel requested. SAM removes the flight at {PROVIDER[target]} once it has found it,
            then releases the campaign.
          </p>
        )}
        {unconfirmed && (
          <p style={{ fontSize: 12.5, lineHeight: 1.45, margin: '6px 0 0' }}>
            Nothing on this page releases it: duplicate the campaign to run the new dates.
          </p>
        )}
        {row.status === 'failed' && (row.failure || row.last_error) && (
          <div style={{ marginTop: 12 }}>
            <FailureNotice
              failure={row.failure}
              fallback={row.last_error}
              announce={false}
              onRetry={() => {
                void run('retry');
              }}
              retrying={retry.isPending}
            />
          </div>
        )}
        {confirming && (
          <div className="extend-flight-confirm" style={{ marginTop: 12 }}>
            Cancel this extension? The campaign keeps the flights it has.
            {(row.create_state === 'dispatched' || row.create_state === 'unknown') &&
              ` SAM finishes the cancel only once it has found and removed the flight at ${PROVIDER[target]}.`}
          </div>
        )}
        {error && (
          <div style={{ marginTop: 12 }}>
            <FailureNotice failure={error.failure} fallback={error.message} />
          </div>
        )}
      </div>
      <div className="modal-foot">
        {confirming ? (
          <>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => setConfirmCancel(false)}
            >
              Keep it
            </button>
            <button
              type="button"
              className="btn"
              style={{ color: 'var(--bad)' }}
              disabled={busy}
              onClick={() => {
                void run('cancel');
              }}
            >
              {cancel.isPending ? 'Cancelling…' : 'Cancel extension'}
            </button>
          </>
        ) : (
          <>
            {canCancel && (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => setConfirmCancel(true)}
              >
                Cancel extension…
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={onClose}>
              Close
            </button>
          </>
        )}
      </div>
    </>
  );
}
